import { Link } from "react-router-dom";
import { useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { StatCard } from "../../components/StatCard/StatCard.js";
import { Skeleton } from "../../components/Skeleton/Skeleton.js";
import { useLatestRuns, useRunFailures, useRunHistory } from "../../hooks/useRuns.js";
import { useProjects } from "../../hooks/useProjects.js";
import type { Run } from "../../lib/types.js";
import styles from "./Dashboard.module.css";

// Palette for per-project lines
const PROJECT_COLORS = [
  "#00e5a0", "#6366f1", "#f59e0b", "#ef4444", "#3b82f6",
  "#ec4899", "#14b8a6", "#a855f7", "#f97316", "#22c55e",
];

function buildChartData(runs: Run[], projects: { id: string; name: string }[]) {
  // Collect all unique days
  const allDays = new Set<string>();
  const byProjectDay = new Map<string, Map<string, number[]>>();

  for (const run of runs) {
    if (!run.summary) continue;
    const day = run.startedAt.slice(0, 10);
    allDays.add(day);
    const projId = (run as Run & { projectId?: string }).projectId ?? "";
    if (!byProjectDay.has(projId)) byProjectDay.set(projId, new Map());
    const dayMap = byProjectDay.get(projId)!;
    const arr = dayMap.get(day) ?? [];
    arr.push(run.summary.score);
    dayMap.set(day, arr);
  }

  const sortedDays = [...allDays].sort().slice(-14);

  return sortedDays.map((day) => {
    const entry: Record<string, string | number> = { date: day.slice(5) };
    // Global average across all projects that day
    const allScoresOnDay: number[] = [];
    for (const [, dayMap] of byProjectDay) {
      const scores = dayMap.get(day);
      if (scores) allScoresOnDay.push(...scores);
    }
    if (allScoresOnDay.length > 0) {
      entry["global"] = Math.round(allScoresOnDay.reduce((a, b) => a + b, 0) / allScoresOnDay.length);
    }
    // Per-project
    for (const proj of projects) {
      const dayMap = byProjectDay.get(proj.id);
      const scores = dayMap?.get(day);
      if (scores && scores.length > 0) {
        entry[proj.id] = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
      }
    }
    return entry;
  });
}

export function DashboardPage() {
  const { data: projectsData, isLoading: projectsLoading } = useProjects();
  const { data: latestRuns, isLoading: runsLoading } = useLatestRuns();
  const { data: runHistory } = useRunHistory();
  const { data: failures, isLoading: failuresLoading } = useRunFailures();
  const [showPerProject, setShowPerProject] = useState(false);

  const projects = projectsData?.data ?? [];
  const activeProjects = projects.filter((p) => p.active);
  const activeCount = activeProjects.length;

  const allScores = (latestRuns ?? [])
    .map((r) => r.summary?.score)
    .filter((s): s is number => s !== undefined);
  const globalPassRate =
    allScores.length === 0
      ? 0
      : Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length);

  const failureCount = failures?.length ?? 0;
  const chartData = buildChartData(runHistory ?? [], activeProjects);

  return (
    <div className={styles.page}>
      <h1 className={styles.heading}>Dashboard</h1>

      <div className={styles.stats}>
        {projectsLoading ? (
          <>
            <Skeleton height="96px" />
            <Skeleton height="96px" />
            <Skeleton height="96px" />
          </>
        ) : (
          <>
            <StatCard title="Active Projects" value={activeCount} icon="📁" />
            <StatCard
              title="Global Pass Rate"
              value={`${globalPassRate}%`}
              icon="✓"
              color={globalPassRate > 90 ? "var(--color-pass)" : globalPassRate >= 75 ? "var(--color-warn)" : "var(--color-fail)"}
            />
            <StatCard
              title="Active Failures"
              value={failureCount}
              icon="⚠"
              color={failureCount > 0 ? "var(--color-fail)" : undefined}
            />
          </>
        )}
      </div>

      <div className={styles.chart}>
        <div className={styles.chartHeader}>
          <h2 className={styles.sectionTitle}>Score trend (last 14 days)</h2>
          <div className={styles.chartToggle}>
            <button
              className={`${styles.chartToggleBtn} ${!showPerProject ? styles.chartToggleActive : ""}`}
              onClick={() => setShowPerProject(false)}
            >
              Global
            </button>
            <button
              className={`${styles.chartToggleBtn} ${showPerProject ? styles.chartToggleActive : ""}`}
              onClick={() => setShowPerProject(true)}
            >
              Per project
            </button>
          </div>
        </div>
        {runsLoading ? (
          <Skeleton height="220px" />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <XAxis dataKey="date" stroke="var(--color-text-muted)" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} stroke="var(--color-text-muted)" tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  background: "var(--color-bg-elevated)",
                  border: "1px solid var(--color-border)",
                  borderRadius: "6px",
                  color: "var(--color-text-primary)",
                  fontSize: "12px",
                }}
              />
              {showPerProject && <Legend wrapperStyle={{ fontSize: "11px" }} />}
              {!showPerProject ? (
                <Line
                  type="monotone"
                  dataKey="global"
                  name="Global"
                  stroke="var(--color-accent)"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              ) : (
                activeProjects.map((proj, i) => (
                  <Line
                    key={proj.id}
                    type="monotone"
                    dataKey={proj.id}
                    name={proj.name}
                    stroke={PROJECT_COLORS[i % PROJECT_COLORS.length]}
                    strokeWidth={1.5}
                    dot={false}
                    connectNulls
                  />
                ))
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className={styles.failures}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Recent failures</h2>
          {failureCount > 20 && (
            <Link to="/projects" className={styles.viewAll}>View all →</Link>
          )}
        </div>
        {failuresLoading ? (
          <Skeleton height="200px" />
        ) : failures && failures.length === 0 ? (
          <div className={styles.empty}>🎉 No active failures!</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Check</th>
                  <th>Category</th>
                  <th>URL</th>
                  <th>Since</th>
                </tr>
              </thead>
              <tbody>
                {(failures ?? []).slice(0, 20).map((f, i) => (
                  <tr key={i} onClick={() => (window.location.href = `/runs/${f.runId}`)} className={styles.tableRow}>
                    <td>{f.projectName}</td>
                    <td><code>{f.checkSlug}</code></td>
                    <td>{f.category}</td>
                    <td className={styles.urlCell}>{f.url}</td>
                    <td>{new Date(f.since).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
